import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const COUNTRY_CODES = [
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+1", country: "USA/Canada", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+61", country: "Australia", flag: "🇦🇺" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+65", country: "Singapore", flag: "🇸🇬" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾" },
  { code: "+977", country: "Nepal", flag: "🇳🇵" },
  { code: "+880", country: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", country: "Sri Lanka", flag: "🇱🇰" },
];

export function PhoneInput({
  value = "",
  onChange,
  defaultCountryCode = "+91",
  placeholder = "Enter phone number",
  disabled = false,
  required = false,
  id,
  "data-testid": testId,
  className,
  style,
  showError = false,
}) {
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [localNumber, setLocalNumber] = useState("");

  // Initialize from value prop
  useEffect(() => {
    if (!value) {
      setLocalNumber("");
      // Keep existing country code or default if not set
      return;
    }

    // Check if value starts with any known country code
    // Sort codes by length desc to match longest prefix first (though most here are 2-3 chars)
    const sortedCodes = [...COUNTRY_CODES].sort(
      (a, b) => b.code.length - a.code.length
    );
    const matched = sortedCodes.find((c) => value.startsWith(c.code));

    if (matched) {
      setCountryCode(matched.code);
      setLocalNumber(value.slice(matched.code.length));
    } else {
      // If no match found (maybe legacy data without code), assume it's just the local number
      // and keep the default country code, OR
      // if it looks like a local number, just set it.
      // But if we want to be safe, maybe we should just show it all in local input if no code matches?
      // For now, let's assume if it doesn't match a code, it's a local number for the currently selected code
      // or just put it all in local number if it's legacy data.
      setLocalNumber(value);
    }
  }, [value]);

  const handleCountryChange = (newCode) => {
    setCountryCode(newCode);
    if (onChange) {
      onChange(`${newCode}${localNumber}`);
    }
  };

  const handleLocalChange = (e) => {
    // Strip non-numeric characters and limit to 10 digits
    const rawValue = e.target.value;
    const numericOnly = rawValue.replace(/\D/g, '');
    const limitedValue = numericOnly.slice(0, 10);

    setLocalNumber(limitedValue);
    if (onChange) {
      // If empty, return empty string so we don't just send "+91"
      if (!limitedValue) {
        onChange("");
      } else {
        onChange(`${countryCode}${limitedValue}`);
      }
    }
  };



  return (
    <div className={className} style={style}>
      <div className="flex gap-2">
        <Select
          value={countryCode}
          onValueChange={handleCountryChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-[100px] flex-shrink-0" data-testid={`${testId}-code`}>
            <SelectValue placeholder="Code" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                <span className="mr-2">{c.flag}</span>
                {c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id={id}
          data-testid={testId}
          type="tel"
          placeholder={placeholder}
          value={localNumber}
          onChange={handleLocalChange}
          disabled={disabled}
          required={required}
          className="flex-1"
        />
      </div>
      {showError && localNumber && localNumber.length !== 10 && (
        <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
          Phone number must be exactly 10 digits
        </p>
      )}
    </div>
  );
}

PhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  defaultCountryCode: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  id: PropTypes.string,
  "data-testid": PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  showError: PropTypes.bool
};
